import { useEffect, useState } from "react";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import api, { formatApiError } from "@/lib/api";
import { Store, Plus, Trash2, Building2, Pencil } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { dealership_name: "", dealership_address: "", contact_name: "", email: "", password: "", phone: "" };

export default function StaffPartners() {
  const [partners, setPartners] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const load = () => api.get("/partners").then(({ data }) => setPartners(data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.dealership_name || !form.email || !form.password) { toast.error("Dealership, email and password are required"); return; }
    if (form.password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setBusy(true);
    try {
      await api.post("/partners", form);
      toast.success("Partner account created");
      setOpen(false); setForm(EMPTY); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not create partner"); }
    finally { setBusy(false); }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Remove partner "${name}"? Their login will be revoked.`)) return;
    await api.delete(`/partners/${id}`); toast.success("Partner removed"); load();
  };
  const openEdit = (partner) => {
    setEditing(partner);
    setEditForm({ ...EMPTY, ...partner, contact_name: partner.contact_name || partner.name || "", password: "" });
  };
  const saveEdit = async () => {
    if (!editForm.dealership_name || !editForm.dealership_address || !editForm.contact_name || !editForm.phone || !editForm.email) { toast.error("Complete all dealer contact fields"); return; }
    setBusy(true);
    try {
      await api.patch(`/partners/${editing.user_id}`, { dealership_name: editForm.dealership_name, dealership_address: editForm.dealership_address, contact_name: editForm.contact_name, phone: editForm.phone, email: editForm.email });
      toast.success("Partner dealer profile updated · partner notified"); setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not update partner"); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      <Navbar />
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-display text-3xl font-bold tracking-tight text-slate-950 dark:text-slate-50">Partner dealers</h1>
            <p className="mt-1 text-slate-600 dark:text-slate-300">Trusted dealers who list inventory directly. Their vehicles publish instantly with a Dealer Listed badge.</p>
          </div>
          <Button onClick={() => { setForm(EMPTY); setOpen(true); }} className="rounded-md bg-slate-950 font-semibold hover:bg-slate-800" data-testid="add-partner-btn"><Plus className="mr-1.5 h-4 w-4" /> Add partner</Button>
        </div>

        {partners === null ? (
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-32 rounded-lg" />)}</div>
        ) : partners.length === 0 ? (
          <div className="mt-10 rounded-xl border border-dashed border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 p-12 text-center" data-testid="partners-empty">
            <Store className="mx-auto h-10 w-10 text-slate-300" />
            <p className="mt-3 font-display text-lg font-bold text-slate-900 dark:text-slate-100">No partner dealers yet</p>
            <p className="text-sm text-slate-500 dark:text-slate-400">Invite a trusted dealer to list inventory directly.</p>
          </div>
        ) : (
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {partners.map((p) => (
              <div key={p.user_id} data-testid={`partner-card-${p.user_id}`} className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-md bg-indigo-50 text-indigo-700"><Building2 className="h-5 w-5" /></div>
                    <div>
                      <p className="font-display font-bold text-slate-900 dark:text-slate-100">{p.dealership_name}</p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">{p.contact_name || p.name}</p>
                    </div>
                  </div>
                  <div className="flex gap-1"><Button size="sm" variant="ghost" onClick={() => openEdit(p)} data-testid={`edit-partner-${p.user_id}`}><Pencil className="h-4 w-4 text-slate-600" /></Button><Button size="sm" variant="ghost" onClick={() => remove(p.user_id, p.dealership_name)} data-testid={`remove-partner-${p.user_id}`}><Trash2 className="h-4 w-4 text-red-500" /></Button></div>
                </div>
                <div className="mt-4 space-y-1 border-t border-slate-100 dark:border-slate-800 pt-3 text-sm text-slate-600 dark:text-slate-300">
                  <p>{p.email}</p>
                  {p.dealership_address && <p>{p.dealership_address}</p>}
                  {p.phone && <p>{p.phone}</p>}
                  <p className="font-semibold text-slate-800 dark:text-slate-200">{p.vehicle_count} vehicle{p.vehicle_count !== 1 ? "s" : ""} listed</p>
                </div>
              </div>
            ))}
          </div>
        )}

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="sm:max-w-md" aria-describedby={undefined}>
            <DialogHeader><DialogTitle className="font-display">Add partner dealer</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div><Label className="text-xs font-bold uppercase text-slate-500 dark:text-slate-400">Dealership name *</Label><Input data-testid="partner-dealership" value={form.dealership_name} onChange={(e) => set("dealership_name", e.target.value)} className="mt-1.5" placeholder="Audi DTLA" /></div>
              <div><Label className="text-xs font-bold uppercase text-slate-500 dark:text-slate-400">Contact person</Label><Input data-testid="partner-contact" value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)} className="mt-1.5" placeholder="Jane Doe" /></div>
              <div><Label className="text-xs font-bold uppercase text-slate-500 dark:text-slate-400">Dealer address</Label><Input data-testid="partner-address" value={form.dealership_address} onChange={(e) => set("dealership_address", e.target.value)} className="mt-1.5" placeholder="Street, city, state ZIP" /></div>
              <div><Label className="text-xs font-bold uppercase text-slate-500 dark:text-slate-400">Login email *</Label><Input data-testid="partner-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="mt-1.5" placeholder="sales@dealer.com" /></div>
              <div><Label className="text-xs font-bold uppercase text-slate-500 dark:text-slate-400">Temporary password *</Label><Input data-testid="partner-password" type="text" value={form.password} onChange={(e) => set("password", e.target.value)} className="mt-1.5" placeholder="Share this with the dealer" /></div>
              <div><Label className="text-xs font-bold uppercase text-slate-500 dark:text-slate-400">Phone</Label><Input data-testid="partner-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="mt-1.5" placeholder="(213) 555-0100" /></div>
            </div>
            <DialogFooter><Button onClick={create} disabled={busy} className="rounded-md bg-slate-950 font-semibold hover:bg-slate-800" data-testid="save-partner-btn">{busy ? "Creating…" : "Create partner"}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
        <Dialog open={!!editing} onOpenChange={(value) => { if (!value) setEditing(null); }}>
          <DialogContent className="sm:max-w-md" aria-describedby={undefined} data-testid="edit-partner-dialog">
            <DialogHeader><DialogTitle className="font-display">Edit partner dealer</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div><Label>Dealer name</Label><Input data-testid="edit-partner-dealer-name" value={editForm.dealership_name} onChange={(e) => setEditForm((f) => ({ ...f, dealership_name: e.target.value }))} className="mt-1.5" /></div>
              <div><Label>Dealer address</Label><Input data-testid="edit-partner-address" value={editForm.dealership_address} onChange={(e) => setEditForm((f) => ({ ...f, dealership_address: e.target.value }))} className="mt-1.5" /></div>
              <div className="grid gap-3 sm:grid-cols-2"><div><Label>Contact person</Label><Input data-testid="edit-partner-contact" value={editForm.contact_name} onChange={(e) => setEditForm((f) => ({ ...f, contact_name: e.target.value }))} className="mt-1.5" /></div><div><Label>Phone</Label><Input data-testid="edit-partner-phone" value={editForm.phone} onChange={(e) => setEditForm((f) => ({ ...f, phone: e.target.value }))} className="mt-1.5" /></div></div>
              <div><Label>Email</Label><Input data-testid="edit-partner-email" type="email" value={editForm.email} onChange={(e) => setEditForm((f) => ({ ...f, email: e.target.value }))} className="mt-1.5" /></div>
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button><Button onClick={saveEdit} disabled={busy} data-testid="save-partner-edit" className="bg-slate-950 font-semibold hover:bg-slate-800">{busy ? "Saving…" : "Save changes"}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
